import {useEffect, useMemo, useReducer} from 'react';
import {InboxFilters, Ticket, TicketCounts} from '../types';

type InboxAction = {type: 'patch'; value: Partial<InboxFilters>} | {type: 'reset'} | {type: 'selection'};
const defaults: InboxFilters = {status: 'all', query: '', priority: 'all', category: 'all', mobileDetail: false};
interface InboxController {
  filters: InboxFilters; filtered: Ticket[]; counts: TicketCounts; active: Ticket | undefined;
  patch: (value: Partial<InboxFilters>) => void; reset: () => void;
}

function inboxReducer(state: InboxFilters, action: InboxAction): InboxFilters {
  if (action.type === 'reset') return {...defaults};
  if (action.type === 'selection') return {...defaults, mobileDetail: true};
  return {...state, ...action.value};
}

export function ticketMatches(ticket: Ticket, filters: InboxFilters): boolean {
  const checks = [
    filters.status === 'all' || ticket.status === filters.status,
    filters.priority === 'all' || ticket.priority === filters.priority,
    filters.category === 'all' || ticket.category === filters.category,
    `${ticket.subject} ${ticket.message} ${ticket.number}`.toLowerCase().includes(filters.query.toLowerCase()),
  ];
  return checks.every(Boolean);
}

function ticketCounts(tickets: Ticket[]): TicketCounts {
  const counts: TicketCounts = {all: tickets.length, new: 0, drafted: 0, resolved: 0};
  tickets.forEach(ticket => { counts[ticket.status] += 1; });
  return counts;
}

export function useInbox(tickets: Ticket[], selected: string | null): InboxController {
  const [filters, dispatch] = useReducer(inboxReducer, defaults);
  const filtered = useMemo(() => tickets.filter(ticket => ticketMatches(ticket, filters)), [tickets, filters]);
  const counts = useMemo(() => ticketCounts(tickets), [tickets]);
  useEffect(() => { if (selected) dispatch({type: 'selection'}); }, [selected, dispatch]);
  const patch = (value: Partial<InboxFilters>): void => dispatch({type: 'patch', value});
  const reset = (): void => dispatch({type: 'reset'});
  const active = filtered.find(ticket => ticket.id === selected) ?? filtered[0];
  return {filters, filtered, counts, active, patch, reset};
}